import * as yup from "yup";
import { UnitOfSaleOutcome } from "../../form/types";
import {
  createUrlSchema,
  urlRegex,
  websiteUrlRegex,
  createLatitudeSchema,
  createLongitudeSchema,
  interventionsSchema,
  riskMonitoringSchema,
  solutionFormDependencies,
  pillarsSchema,
  commercialInformationSchema,
  narrativeSchema,
  shortDescriptionSchema,
  satelliteImagerySchema,
  assessmentReportSchema,
} from "../../form/sharedSchema";
import { outcomesSchema } from "../../form/sharedOutcomesSchema";
import { transformTimestampValidity } from "../../formatting/date-time-manipulation";

const addProgrammeInfoSchema = yup.object().shape({
  "asset-type": yup
    .string()
    .nullable()
    .required("Asset (credit) type is a required field"),
  "description-document": createUrlSchema(
    urlRegex,
    "Must be a valid URL",
    false,
  ),
  "carbon-crediting-programme": yup.array().of(yup.string()),
  "carbon-accounting-methodology": yup.string(),
});

const addInternalDetailsSchema = yup.object().shape({
  "website-url": createUrlSchema(
    websiteUrlRegex,
    "Please paste the Marketing website url starting from 'projects', example: projects/mangroves-maroalika or archived-projects/mangroves-maroalika",
    false,
  ),
  "xero-account-number": yup
    .string()
    .max(10, "Can't be longer than 10 characters"),
  "research-team-notes": yup.string(),
  "additional-resources-link": createUrlSchema(
    urlRegex,
    "Must be a valid URL",
    false,
  ),
});

export const addSolutionFormSchema = yup.object().shape(
  {
    "solution-name": yup.string().required("Project name is required"),
    "solution-vbc-website-display-name": yup.string().when("outcomes", {
      is: (outcomes: Array<UnitOfSaleOutcome> | undefined) =>
        outcomes?.some(
          (outcome) =>
            outcome.unit === "Biodiversity" && outcome["unit-of-sale"],
        ) ?? false,
      then: (schema) =>
        schema.required(
          "VBC Website Display Name is required when Biodiversity is the unit of sale",
        ),
    }),
    "solution-developer": yup.string(),
    "solution-short-description": shortDescriptionSchema,
    "country-select": yup.array().of(yup.string()).nullable(),
    "solution-development-stage": yup.array().of(yup.string()),
    "solution-assessment-methodology": yup
      .string()
      .nullable()
      .required("Assessment methodology is required"),
    "solution-intervention-type": yup
      .string()
      .nullable()
      .required("Intervention type is a required field"),
    "solution-is-ready-for-sale": yup.boolean(),
    "solution-not-ready-for-sale-reason": yup
      .string()
      .nullable()
      .when("solution-is-ready-for-sale", {
        is: false,
        then: (schema) =>
          schema.required("Not ready for sale reason must be provided"),
        otherwise: (schema) => schema.strip(),
      }),
    "solution-sdg": yup.array().of(yup.number()),
    "location-pin-map-link": createUrlSchema(
      urlRegex,
      "Must be a valid URL",
      true,
    ),
    "solution-start-date": yup
      .boolean()
      .transform(transformTimestampValidity)
      .isFalse("Must be a valid date in dd/mm/yyyy"),
    "solution-end-date": yup
      .boolean()
      .transform(transformTimestampValidity)
      .isFalse("Must be a valid date in dd/mm/yyyy"),
    latitude: createLatitudeSchema(false),
    longitude: createLongitudeSchema(false),
    "solution-is-carbon-offset": yup.boolean(),
    interventions: interventionsSchema,
    outcomes: outcomesSchema,
    pillars: pillarsSchema,
    "programme-info": addProgrammeInfoSchema,
    "internal-details": addInternalDetailsSchema,
    "commercial-information": commercialInformationSchema,
    narrative: narrativeSchema,
    assessmentReport: assessmentReportSchema,
    "satellite-imagery": satelliteImagerySchema,
    "risk-monitoring": riskMonitoringSchema,
  },
  solutionFormDependencies,
);
